import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ArrowRight, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useReducedMotion } from "@/shared/motion/useReducedMotion";
import { useAuth, useModuleHistoryVisible, useModuleLocked } from "@/contexts/AuthContext";
import { useImpersonationOptional } from "@/contexts/ImpersonationContext";
import { useAdvisorAccess } from "@/entities/advisor-chat";
import { useAdvisorLauncher } from "@/widgets/AdvisorLauncher";
import { AdvisorRobot } from "@/widgets/AdvisorLauncher/AdvisorRobot";
import { useActiveSkin } from "@/shared/hooks/useTheme";
import { isTenantManagerPlus, ROLES, type RoleKey } from "@/shared/lib/rbac";
import { InFlightRow } from "@/entities/work/ui/InFlightRow";
import { RecentWorksRow } from "@/entities/work/ui/RecentWorksRow";
import type { WorkLineKey } from "@/entities/work/model/types";
import {
  CLUSTER_ORDER,
  CLUSTER_META,
  clusterRoute,
  openExternalCabin,
  presentationOf,
} from "@/entities/capability";
import { capFigureOf, deptFigureOf } from "@/entities/scene-card/model/figures";
import PlatformHome from "@/features/platform-home/ui/PlatformHomePage";
import { ClusterRowCard } from "./workbench/ClusterRowCard";
import { useClusterCards } from "./workbench/useClusterCards";
import { TenantLowBalanceBanner } from "@/features/billing/ui/LowBalanceBanner";
import { SubscriptionLapseBanner, TenantExpiryBanner } from "@/features/billing/ui/ExpiryBanner";
import { greetingName } from "@/shared/lib/greetingName";
import { CsQuotaExhaustedBanner } from "@/features/customer-service/ui/CsQuotaExhaustedBanner";

/**
 * 营销智脑首页(2026-07-25 发现流)—— **发现总览**。
 *
 * ═══ 三段(小阳=旁路可选,手动为主线)═══════════════════════════════════════════
 *   ① 小阳 chat-first hero  —— 一句话派活(可选副驾;无 advisor 权限→退化成问候条,主线不缺)
 *   ② 在途 / 最近           —— 复用 InFlightRow / RecentWorksRow(不动逻辑)
 *   ③ 5 群**横滑卡带**       —— 每群一行(露 3-5 张紧凑卡 + 「查看全部 →」进群页)
 *
 * ★为什么横滑带而不是竖直全铺(老稿 6000px 长滚的来源):17 舱全铺一页 = 太多反而看不清。
 *   发现层压成每群一行、一屏扫完 5 群;要看全 / 要干活 → 点「查看全部」进**群页**(/g/:cluster,
 *   一次只聚焦一个群的完整卡墙)。dept 舱与能力舱在首页行里统一成紧凑卡(ClusterRowCard),
 *   富部门卡(主角图/积分标/hover 真样图)只在群页出现。归组/门控/计数走 useClusterCards 单源。
 */
export default function Dashboard() {
  const navigate = useNavigate();
  const { user, effectiveRole, effectiveScope } = useAuth();
  const { t } = useTranslation();
  const { openAdvisor, setScrolledPastHero } = useAdvisorLauncher();
  const { canUseAdvisor } = useAdvisorAccess();
  const skin = useActiveSkin();

  const ROLE_GREETING: Record<RoleKey, string> = {
    [ROLES.PLATFORM_ADMIN]: t("pages.index.greetingPlatformAdmin"),
    [ROLES.TENANT_ADMIN]: t("pages.index.greetingTenantAdmin"),
    [ROLES.TENANT_MANAGER]: t("pages.index.greetingTenantManager"),
    [ROLES.TENANT_STAFF]: t("pages.index.greetingTenantStaff"),
    [ROLES.MEMBER]: "",
    // 渠道管理员不落首页(ProtectedRoute 把 / 转到 /partner),占位空串。
    [ROLES.PARTNER_ADMIN]: "",
  };

  // 代客时 effectiveScope=TENANT → 租户工作台;非代客平台超管 PLATFORM → PlatformHome。
  const isPlatform = effectiveScope === "PLATFORM";
  const { isLoading, forCluster, moduleReady, visible } = useClusterCards({ enabled: !isPlatform });

  const role = effectiveRole ?? user?.role ?? ROLES.TENANT_STAFF;
  const greeting = ROLE_GREETING[role] ?? "";
  // 1004 O8:代客时 user 是运营本人(平台超管 / 渠道管理员),问候语不能叫出「平台超管」—— 改叫被代客的客户企业名,
  // 拿不到就只说「您好」。
  const { isImpersonating, meta: impersonationMeta } = useImpersonationOptional();
  const displayName = isImpersonating
    ? impersonationMeta?.tenantName?.trim() || ""
    : // 1005:没填联系人的渠道租户 displayName 会回落成邮箱 —— 回落顺序 姓名 → 企业名 → 「您好」
      greetingName(user);

  // ② 在途与最近:停用模块的历史产物仍展示。OEM H1 起 visible(= /me/permissions)已与租户开通求交,
  //   未开通 / 停用 / 过期的模块改由 historyModules 补回(useModuleHistoryVisible);入口类(卡墙 / chip)不受影响。
  const posterHistory = useModuleHistoryVisible("ai_poster");
  const videoHistory = useModuleHistoryVisible("ai_video");
  const dhHistory = useModuleHistoryVisible("ai_digital_human");
  const lockedLine: Partial<Record<WorkLineKey, boolean>> = {
    poster: useModuleLocked("ai_poster"),
    video: useModuleLocked("ai_video"),
    dh: useModuleLocked("ai_digital_human"),
  };
  const workLines: WorkLineKey[] = [
    ...(visible.ai_poster || posterHistory ? (["poster"] as const) : []),
    ...(visible.ai_video || videoHistory ? (["video"] as const) : []),
    ...(visible.ai_digital_human || dhHistory ? (["dh"] as const) : []),
  ];
  // 未开通的线:详情页是编辑 / 新建面,改去「作品」页该线(列表内预览、下载,新建动作不可用)。
  const openWork = (w: { line: WorkLineKey; detailRoute: string }) =>
    navigate(lockedLine[w.line] ? `/works?line=${w.line}` : w.detailRoute);

  // hero 快捷 chips:进已开舱的手动快捷。★用 moduleReady(权限 + 未停用)——修旧 P3:
  //   chip 是创作入口,不能对停用的舱亮着(与部门卡同口径,不给点了落地页却 gate 的假快捷)。
  const quickChips = [
    { labelKey: "dept.explosive", route: "/ai-explosive", moduleKey: "ai_explosive_parse" },
    { labelKey: "dept.poster", route: "/ai-haibao", moduleKey: "ai_poster" },
    { labelKey: "cabin.name.geo_radar", route: "/geo-radar", moduleKey: "geo_radar" },
    { labelKey: "dept.video", route: "/ai-video", moduleKey: "ai_video" },
  ].filter((c) => moduleReady(c.moduleKey));

  // 动效:hero 入场 + scroll-dock 飞入(只动 transform/opacity,三皮肤 token 安全)。
  const reduced = useReducedMotion();
  const heroRef = useRef<HTMLDivElement>(null);
  const heroFaceRef = useRef<HTMLSpanElement>(null);
  const [docked, setDocked] = useState(false);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const past = window.scrollY > 120;
        setDocked(past);
        setScrolledPastHero(past);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
      setScrolledPastHero(false);
    };
  }, [setScrolledPastHero]);

  useGSAP(
    () => {
      const el = heroRef.current;
      if (!el || reduced) return;
      gsap.from(el, { opacity: 0, y: 16, duration: 0.5, ease: "power2.out" });
    },
    { scope: heroRef, dependencies: [reduced] }
  );

  useGSAP(
    () => {
      const el = heroFaceRef.current;
      if (!el) return;
      if (!docked) {
        if (reduced) gsap.set(el, { x: 0, y: 0, scale: 1, opacity: 1, clearProps: "transform" });
        else gsap.to(el, { x: 0, y: 0, scale: 1, opacity: 1, duration: 0.4, ease: "power2.out" });
        return;
      }
      if (reduced) {
        gsap.set(el, { opacity: 0 });
        return;
      }
      const ball = document.querySelector("[data-advisor-launcher]");
      const from = el.getBoundingClientRect();
      const target = ball?.getBoundingClientRect();
      const dx = target ? target.left + target.width / 2 - (from.left + from.width / 2) : 60;
      const dy = target ? target.top + target.height / 2 - (from.top + from.height / 2) : 60;
      gsap.to(el, { x: dx, y: dy, scale: 0.4, opacity: 0, duration: 0.5, ease: "power2.in" });
    },
    { dependencies: [docked, reduced] }
  );

  if (isPlatform) {
    return <PlatformHome />;
  }

  return (
    <div className="space-y-6">
      {/* 0926 修复波 F:积分池 < 5% 的站内提示(仅租户超管读得到余额,其余角色不渲染) */}
      <TenantLowBalanceBanner />
      {/* 0926 第八批 Z3:套餐 ≤15 天到期 / 已到期(仅租户超管,可当天关掉) */}
      {/* 1005:套餐已到期 / 停用 —— 全员常驻(此前员工首页只见内容生产整块消失,毫无解释) */}
      <SubscriptionLapseBanner />
      <TenantExpiryBanner />
      {/* 0926 修复波第五批:客服配额当天耗尽(AI 不再回复客户)—— 仅租户超管,可当天关掉 */}
      <CsQuotaExhaustedBanner />
      {/* ① 小阳 hero —— 一句话派活(小阳=旁路可选;无 advisor 权限退化为问候条) */}
      <section
        ref={heroRef}
        className="relative overflow-hidden rounded-[calc(var(--radius)*1.4)] border bg-gradient-to-br from-primary/12 via-primary/5 to-transparent px-6 py-5"
      >
        <div className="flex items-start gap-4">
          {canUseAdvisor && (
            <span
              ref={heroFaceRef}
              data-hero-face
              className="relative hidden h-16 w-16 shrink-0 sm:block"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                <AdvisorRobot variant={skin} className="h-11 w-11" />
              </span>
              <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-success ring-2 ring-card" />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <h1 className="text-xl font-bold tracking-tight">
                {displayName
                  ? t("pages.index.heroPrompt", { name: displayName })
                  : t("pages.index.heroPromptNoName")}
              </h1>
              {/* 问候条右侧的两条文字链。刻意都留成文字 —— 卡墙上的卡才是主入口,这里只是就近的快捷。 */}
              <div className="flex shrink-0 items-center gap-3">
                {/* 新手指引(2026-09-19 §21.1):不挂角色门 —— 最需要它的恰恰是权限最小的那一档。
                    本段只在租户 scope 渲染(平台超管上面已 return PlatformHome)。 */}
                <button
                  type="button"
                  onClick={() => navigate("/help")}
                  className="whitespace-nowrap text-xs text-muted-foreground transition-colors hover:text-primary"
                >
                  {t("pages.index.heroGuide")}
                </button>
                {/* 经营看板(2026-07-30 四合一):看盘/对话记录/记忆/技能的入口。
                    MANAGER+ 才给(STAFF 无 /advisor 路由白名单,给了就是点了 403 的假入口)。 */}
                {isTenantManagerPlus(effectiveRole) && (
                  <button
                    type="button"
                    onClick={() => navigate("/advisor")}
                    className="whitespace-nowrap text-xs text-muted-foreground transition-colors hover:text-primary"
                  >
                    {t("pages.index.heroHub")}
                  </button>
                )}
              </div>
            </div>
            <p className="mt-1 max-w-2xl text-sm text-foreground/70">
              {canUseAdvisor ? t("pages.index.heroSay") : greeting}
            </p>

            {canUseAdvisor && (
              <button
                type="button"
                onClick={() => openAdvisor()}
                className="mt-3 flex h-11 w-full max-w-xl items-center gap-2 rounded-[calc(var(--radius)*1.1)] border bg-card px-4 text-left text-[13px] text-muted-foreground transition-colors hover:border-primary/60"
              >
                <span className="flex-1 truncate">{t("pages.index.heroAsk")}</span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Send className="h-4 w-4" />
                </span>
              </button>
            )}

            {quickChips.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {canUseAdvisor && (
                  <span className="text-2xs font-semibold text-primary">
                    {t("pages.index.heroTry")}
                  </span>
                )}
                {quickChips.map((c) => (
                  <button
                    key={c.route}
                    type="button"
                    onClick={() => navigate(c.route)}
                    className="rounded-full border bg-card px-3 py-1 text-xs transition-colors hover:border-primary hover:text-primary"
                  >
                    {t(c.labelKey)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ② 在途与最近 —— 复用现有行(不动逻辑) */}
      {workLines.length > 0 && (
        <>
          <InFlightRow lines={workLines} onOpen={openWork} />
          <RecentWorksRow
            lines={workLines}
            onOpen={openWork}
            onViewAll={(href) => navigate(href)}
          />
        </>
      )}

      {/* ③ 5 群横滑卡带 —— 发现层主线;点「查看全部 →」进群页 */}
      {CLUSTER_ORDER.map((cluster) => {
        const { depts, expiredDepts = [], caps, counts } = forCluster(cluster);
        const showSkeleton = cluster === "content" && isLoading && depts.length === 0;
        if (depts.length + expiredDepts.length + caps.length === 0 && !showSkeleton) return null;
        const meta = CLUSTER_META[cluster];

        const countParts: string[] = [];
        if (counts.active > 0) countParts.push(`${counts.active} ${t("cabin.status.active")}`);
        if (counts.integrating > 0)
          countParts.push(`${counts.integrating} ${t("cabin.status.integrating")}`);
        if (counts.coming > 0) countParts.push(`${counts.coming} ${t("cabin.status.coming")}`);
        if (expiredDepts.length > 0) countParts.push(`${expiredDepts.length} 已到期`);

        return (
          <section key={cluster} className="work-row">
            <div className="mb-3 flex items-center gap-2">
              <span className="h-3.5 w-[3px] rounded-sm bg-primary" />
              <h2 className="text-sm font-semibold">{t(meta.labelKey)}</h2>
              <span className="hidden text-xs text-muted-foreground sm:inline">
                {t(meta.subKey)}
              </span>
              <span className="ml-auto text-xs text-muted-foreground">
                {countParts.join(" · ")}
              </span>
              <button
                type="button"
                onClick={() => navigate(clusterRoute(cluster))}
                data-cluster-all={cluster}
                aria-label={t("pages.index.viewAllClusterAria", { name: t(meta.labelKey) })}
                className="flex items-center gap-0.5 whitespace-nowrap text-xs font-semibold text-primary transition-colors hover:text-primary/80"
              >
                {t("pages.index.viewAllCluster")}
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>

            {showSkeleton ? (
              <div className="cardwall-row">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="cardwall-row-item">
                    <div className="h-[76px] animate-pulse rounded-t-[calc(var(--radius)*1.5)] bg-muted" />
                    <div className="space-y-2 p-3">
                      <div className="h-3.5 w-20 animate-pulse rounded bg-muted" />
                      <div className="h-3 w-full animate-pulse rounded bg-muted/60" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="cardwall-row">
                {depts.map((d) => (
                  <div key={d.moduleKey} className="cardwall-row-item">
                    <ClusterRowCard
                      dataKey={d.moduleKey}
                      name={t(d.titleKey, { defaultValue: d.title })}
                      desc={d.desc}
                      icon={d.icon}
                      status="active"
                      kind={d.tagline}
                      fig={deptFigureOf(skin, d.hub)}
                      onOpen={() => navigate(d.route)}
                    />
                  </div>
                ))}
                {/* 1005:套餐到期关掉的内容舱照样上墙,标「已到期」、不可进(顶部常驻提示条说明原因与去处) */}
                {expiredDepts.map((d) => (
                  <div key={d.moduleKey} className="cardwall-row-item">
                    <ClusterRowCard
                      dataKey={d.moduleKey}
                      name={t(d.titleKey, { defaultValue: d.title })}
                      desc={d.desc}
                      icon={d.icon}
                      status="active"
                      expired
                      kind={d.tagline}
                      fig={deptFigureOf(skin, d.hub)}
                    />
                  </div>
                ))}
                {caps.map((c) => {
                  const pres = presentationOf(c.capability);
                  if (!pres) return null;
                  const active = c.status === "active";
                  return (
                    <div key={c.capability} className="cardwall-row-item">
                      <ClusterRowCard
                        dataKey={c.capability}
                        name={t(`cabin.name.${c.capability}`, { defaultValue: c.displayName })}
                        desc={t(`cabin.desc.${c.capability}`, {
                          defaultValue: c.description ?? "",
                        })}
                        icon={pres.icon}
                        status={c.status}
                        kind={pres.kindKey ? t(pres.kindKey) : undefined}
                        fig={capFigureOf(skin, c.imageKey ?? c.capability)}
                        // 外站舱(漫剧工坊):首页这条带是紧凑统一卡,不另做一种卡,
                        // 但「点了会离开本站」要说清 —— 标 + CTA + aria 由卡自己画(0920 统一批)。
                        external={!pres.route && !!pres.externalUrl}
                        // 落点口径与群页 ClusterPage.openOf **同一个函数**:站内 route 走 SPA;
                        // externalUrl 新开标签页。0918 老板实测「首页点漫剧不跳」—— 这里此前只认
                        // route,外站舱在首页是一张没有 onOpen 的死卡,而群页能点;两份逐字相同的
                        // 代码就是双源,0920 收进 openExternalCabin 一处。
                        onOpen={
                          !active
                            ? undefined
                            : pres.route
                              ? () => navigate(pres.route!)
                              : pres.externalUrl
                                ? () => openExternalCabin(pres.externalUrl!)
                                : undefined
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
